import { clientLabel } from '@mdt/domain';
import { command } from './api.ts';

/**
 * Web Push on the client (roadmap 3.5). Permission is asked only after the person pressed "Enable
 * notifications" on the explanation screen, never on load. Shared mode never gets here (the settings
 * screen does not offer it and push.subscribe is FORBIDDEN there).
 */
export type PushSupport = 'supported' | 'unsupported' | 'install-first';

export function isStandalone(): boolean {
  return globalThis.matchMedia?.('(display-mode: standalone)').matches === true
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export const isAppleMobile = () => /\b(iPhone|iPad|iPod)\b/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function pushSupport(): PushSupport {
  const hasApis = 'serviceWorker' in navigator && 'PushManager' in globalThis && 'Notification' in globalThis;
  // iOS and iPadOS offer Web Push only to web apps added to the Home Screen (16.4+).
  if (isAppleMobile() && !isStandalone()) return 'install-first';
  return hasApis ? 'supported' : 'unsupported';
}

export const permission = (): NotificationPermission | 'unsupported' => ('Notification' in globalThis ? Notification.permission : 'unsupported');

function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker?.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export class PushError extends Error {
  readonly reason: 'denied' | 'failed';
  constructor(reason: 'denied' | 'failed') {
    super(reason);
    this.reason = reason;
  }
}

/** Asks permission, subscribes this browser and registers it. Undoes the browser half if the server refuses. */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  const result = await Notification.requestPermission();
  if (result !== 'granted') throw new PushError('denied');
  const reg = await navigator.serviceWorker.ready;
  let sub: PushSubscription;
  try {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(vapidPublicKey) });
  } catch {
    throw new PushError('failed');
  }
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  try {
    const label = clientLabel(navigator.userAgent);
    await command('push.subscribe', { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, ...(label ? { deviceLabel: label } : {}) });
  } catch (e) {
    await sub.unsubscribe().catch(() => {});
    throw e;
  }
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await command('push.unsubscribe', { endpoint: sub.endpoint });
  await sub.unsubscribe();
}
